import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { playNotificationSound } from "@/hooks/useNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/** Polls the signed-in user's own notification feed; new unseen rows beep and surface together as `arrived`. */
export function useNotificationCenter({ enabled, pollInterval = 15000 }) {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [arrived, setArrived] = useState(null);
  const seenIds = useRef(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/notifications`, opts);
    setItems(data.items);
    setUnreadCount(data.unread_count);
    const ids = new Set(data.items.map(n => n.id));
    if (seenIds.current !== null) {
      const fresh = data.items.filter(n => !seenIds.current.has(n.id) && !n.read_at);
      if (fresh.length > 0) {
        playNotificationSound();
        setArrived(fresh);
      }
    }
    seenIds.current = ids;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const poll = async () => {
      try { if (!cancelled) await load(); } catch {}
    };
    poll();
    const timer = setInterval(poll, pollInterval);
    return () => { cancelled = true; clearInterval(timer); };
  }, [enabled, pollInterval, load]);

  const markRead = useCallback(async (id) => {
    await axios.post(`${API}/notifications/${id}/read`, {}, opts);
    await load();
  }, [load]);

  const markAllRead = useCallback(async () => {
    await axios.post(`${API}/notifications/read-all`, {}, opts);
    await load();
  }, [load]);

  return { items, unreadCount, arrived, markRead, markAllRead };
}
